/**
 * Planos (ADR-G19). O plano vale por espaço e decide-se na base de dados
 * (app.workspace_plan): a API aplica os limites; a app e a extensão só os mostram.
 *
 * - Grátis: espaço pessoal, até 10 guias, gravar, editar, guiar e PDF.
 * - Pro (por conta): guias ilimitados, ligações públicas, marca, exportações editoriais,
 *   Executar e IA. Um espaço partilhado em que só a própria pessoa edita, com até 3 leitores.
 * - Equipa (por espaço): editores pagos, leitores ilimitados; 14 dias de teste a quem tem o Pro.
 * - expired: equipa sem plano ativo (teste acabado): só leitura até escolher um plano.
 */
export type PlanId = 'free' | 'pro' | 'team' | 'expired';
export type PlanStatus = 'active' | 'trialing' | 'past_due' | 'canceled';

export interface PlanFeatures {
  /** Máximo de guias no espaço (null = sem limite). */
  guides: number | null;
  /** Espaço para capturas, em MB (equipa: por lugar faturado, ver billedSeats). */
  storageMb: number;
  /** Leitores convidados (null = ilimitados). */
  readers: number | null;
  /** Pode ter outros editores. */
  editors: boolean;
  publicLinks: boolean;
  embed: boolean;
  brand: boolean;
  /** HTML, PowerPoint e Markdown (o PDF é de todos). */
  editorialExports: boolean;
  /** Execução automática. */
  run: boolean;
  ai: boolean;
  /** Pode criar e alterar guias. */
  write: boolean;
}

const PAID = {
  guides: null,
  publicLinks: true,
  embed: true,
  brand: true,
  editorialExports: true,
  run: true,
  ai: true,
  write: true,
} as const;

export const PLANS: Record<PlanId, PlanFeatures> = {
  free: {
    guides: 10,
    storageMb: 500,
    readers: 0,
    editors: false,
    publicLinks: false,
    embed: false,
    brand: false,
    editorialExports: false,
    run: false,
    ai: false,
    write: true,
  },
  pro: { ...PAID, storageMb: 20_480, readers: 3, editors: false },
  team: { ...PAID, storageMb: 10_240, readers: null, editors: true },
  expired: {
    guides: null,
    storageMb: 0,
    readers: 0,
    editors: false,
    publicLinks: false,
    embed: false,
    brand: false,
    editorialExports: false,
    run: false,
    ai: false,
    write: false,
  },
};

/**
 * Preços de referência, **sem IVA** (os definitivos ficam no fornecedor de pagamentos, que soma
 * o IVA do país de quem compra; as empresas com número de IVA válido noutro país da UE não o pagam).
 */
export const PRICES = {
  pro: { monthly: 19, yearly: 192 },
  team: { monthly: 13, yearly: 132, perSeat: true },
} as const;

/** Poupança da faturação anual face a 12 meses, em % (Pro 16 %, Equipa 15 %). */
export const annualSaving = (id: keyof typeof PRICES) =>
  Math.round((1 - PRICES[id].yearly / (PRICES[id].monthly * 12)) * 100);

/** A maior poupança anual dos planos pagos (para o interruptor da página de preços). */
export const maxAnnualSaving = () => Math.max(annualSaving('pro'), annualSaving('team'));

/** IVA português, para mostrar o preço final a quem compra em Portugal. */
export const VAT_PT = 0.23;

/** O plano Equipa paga-se por editor, no mínimo 3 (uma equipa de uma pessoa é o Pro). */
export const TEAM_MIN_EDITORS = 3;

/**
 * Máximo de editores no plano Equipa (o mesmo máximo da quantidade no Paddle: acima disso o
 * Paddle recusava o acerto dos lugares). Para mais, plano para empresas (ADR-G26).
 */
export const TEAM_MAX_EDITORS = 100;

export const TEAM_TRIAL_DAYS = 14;

/** Pedidos de sugestões por IA por espaço e por mês (UTC), nos planos com IA. */
export const AI_MONTHLY_LIMIT = 100;

/** Plano de um espaço, como a API o devolve. */
export interface WorkspacePlan {
  plan: PlanId;
  status: PlanStatus | null;
  trialEndsAt: string | null;
  features: PlanFeatures;
  usage: { guides: number; storageMb: number; editors: number; readers: number };
  /** Limite de espaço efetivo (equipa: por lugar × lugares faturados, mínimo 3). */
  storageLimitMb: number;
}

/** Motivos de um 402 PLAN_LIMIT (fields.reason). */
export type PlanLimitReason =
  | 'guides'
  | 'storage'
  | 'publicLinks'
  | 'brand'
  | 'ai'
  | 'aiQuota'
  | 'readers'
  | 'editors'
  | 'editorsMax'
  | 'expired';

/** Faturação mensal ou anual. */
export type BillingInterval = 'monthly' | 'yearly';

/** Lugares faturados numa equipa: os editores, no mínimo TEAM_MIN_EDITORS. */
export const billedSeats = (editors: number) => Math.max(editors, TEAM_MIN_EDITORS);

/** Subscrição paga de um espaço (GET /v1/workspaces/:id/billing). */
export interface WorkspaceBilling {
  /** Pagamentos online ligados (fornecedor configurado na API). */
  available: boolean;
  /** O que se compra neste espaço: o Pro (espaço pessoal) ou o Equipa. */
  product: 'pro' | 'team';
  /** Pode comprar e gerir: o dono (no espaço pessoal, a própria pessoa). */
  canManage: boolean;
  /** Há uma subscrição paga em vigor. */
  subscribed: boolean;
  status: PlanStatus | null;
  /** Fim do período pago (renovação ou fim, se foi cancelada). */
  currentPeriodEnd: string | null;
  /** Lugares faturados (Equipa). */
  seats: number | null;
}

/** Configuração pública do Paddle.js (GET /public/billing; null = pagamentos desligados). */
export interface BillingConfig {
  clientToken: string;
  environment: 'sandbox' | 'production';
}

/** Checkout aberto na página com o Paddle.js (POST /v1/workspaces/:id/billing/checkout). */
export interface CheckoutSession extends BillingConfig {
  transactionId: string;
  email: string | null;
}
